/**
 * Mock API responses for demo mode. Used by the fetch interceptor when localStorage.demoMode
 * is set to 'true', so the app can run without the real backend.
 */

import { getDemoTier, getDemoCompetences } from './demoMode';


const demoUser = {
  address: 'Rue de la Loi 42, 1000 Brussels',
  business_name: 'Martin & Co SPRL',
  phone_number: '+32123456789',
  first_name: 'Christopher',
  last_name: 'Martin',
  bce: '0123456789',
  iban: 'BE68 5390 0754 7034',
  email: 'christopher@example.com',
  offsetMinutes: 0,
  registration_id: null,
  date_of_birth: '1990-05-15',
  nationality: 'Belgian',
  gender: 'male',
  tax_id: '90.05.15-123.45',
  emergency_name: 'Sophie Martin',
  emergency_phone: '+32498765432',
};

const demoTeamInfo = {
  team_name: 'Brussels Central',
  address: 'Team address',
  bce: '9876543210',
};

function mockCheckToken() {
  return {
    userId: 1,
    firstName: 'Christopher',
    lastName: 'Martin',
    notification_token: 'demo-push-token',
    teams: {
      1: {
        team_name: 'Brussels Central',
        competences: getDemoCompetences(),
        ConfirmationDate: '15',
      },
    },
  };
}

/**
 * Build demo schedule data for the requested month/year (personal planning).
 * month: 0-based (0 = January) as used by JS Date
 */
function mockGetUserSchedule(body) {
  const month = typeof body.month === 'number' ? body.month : new Date().getMonth();
  const year = typeof body.year === 'number' ? body.year : new Date().getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const userWorkInfo = [
    // Confirmed shift with modified arrival time (shows actual vs scheduled highlight)
    { id: 1001, user_id: 1, year, month, day: Math.min(5, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', arrival_time: '09:12:00', departure_time: '17:03:00', working_site: 'Brussels HQ', competence: 'member', pause_time: '01:00:00', confirmed: 1, team_id: 1, note: null, real_pause_time: '00:45:00' },
    // Unconfirmed shift — shows unconfirmed indicator; past date shows "Confirm Work" button
    { id: 1002, user_id: 1, year, month, day: Math.min(8, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', arrival_time: null, departure_time: null, working_site: 'Antwerp Branch', competence: 'member', pause_time: '01:00:00', confirmed: 0, team_id: 1, note: null, real_pause_time: null },
    // Unconfirmed future shift — shows in "Shifts to Sign"
    { id: 1006, user_id: 1, year, month, day: Math.min(12, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', arrival_time: null, departure_time: null, working_site: 'Antwerp Branch', competence: 'member', pause_time: '01:00:00', confirmed: 0, team_id: 1, note: null, real_pause_time: null },
    // Unconfirmed future evening shift — shows in "Shifts to Sign"
    { id: 1007, user_id: 1, year, month, day: Math.min(15, daysInMonth), start_time: '14:00:00', end_time: '22:00:00', arrival_time: null, departure_time: null, working_site: 'Liège Depot', competence: 'member', pause_time: '00:30:00', confirmed: 0, team_id: 1, note: null, real_pause_time: null },
    // Confirmed evening shift
    { id: 1003, user_id: 1, year, month, day: Math.min(28, daysInMonth), start_time: '14:00:00', end_time: '22:00:00', arrival_time: null, departure_time: null, working_site: 'Liège Depot', competence: 'member', pause_time: '00:30:00', confirmed: 1, team_id: 1, note: 'Evening shift', real_pause_time: null },
    // Confirmed WFH
    { id: 1004, user_id: 1, year, month, day: Math.min(24, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', arrival_time: null, departure_time: null, working_site: 'Remote', competence: 'member', pause_time: '01:00:00', confirmed: 1, team_id: 1, note: 'WFH', real_pause_time: null },
    // Confirmed half-day at client site
    { id: 1005, user_id: 1, year, month, day: Math.min(26, daysInMonth), start_time: '09:00:00', end_time: '13:00:00', arrival_time: null, departure_time: null, working_site: 'Client Site A', competence: 'member', pause_time: '00:00:00', confirmed: 1, team_id: 1, note: null, real_pause_time: null },
  ].filter((w) => w.day <= daysInMonth);

  const userAvailabilities = [
    // Day availability, two sites
    { id: 2001, user_id: 1, year, month, day: Math.min(2, daysInMonth), start_time: '09:00:00', end_time: '18:00:00', available_sites: 'Brussels HQ,Antwerp Branch', night_shift: false, date: `${year}-${String(month + 1).padStart(2, '0')}-02` },
    // Day availability, one site
    { id: 2002, user_id: 1, year, month, day: Math.min(9, daysInMonth), start_time: '08:00:00', end_time: '17:00:00', available_sites: 'Ghent Office', night_shift: false, date: `${year}-${String(month + 1).padStart(2, '0')}-09` },
    // Night shift availability
    { id: 2003, user_id: 1, year, month, day: Math.min(16, daysInMonth), start_time: '22:00:00', end_time: '00:00:00', available_sites: 'Liège Depot', night_shift: true, date: `${year}-${String(month + 1).padStart(2, '0')}-16` },
    // Day availability, multiple sites
    { id: 2004, user_id: 1, year, month, day: Math.min(23, daysInMonth), start_time: '10:00:00', end_time: '18:00:00', available_sites: 'Brussels HQ,Remote,Client Site A', night_shift: false, date: `${year}-${String(month + 1).padStart(2, '0')}-23` },
  ].filter((a) => a.day <= daysInMonth);

  const userNeededWork = [
    { id: 3001, team_id: 1, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', month, day: Math.min(7, daysInMonth), year, competence: 'member', working_site: 'Brussels HQ', needed_work_site: 'Brussels HQ' },
    { id: 3002, team_id: 1, start_time: '14:00:00', end_time: '22:00:00', pause_time: '00:30:00', month, day: Math.min(14, daysInMonth), year, competence: 'member', working_site: 'Antwerp Branch', needed_work_site: 'Antwerp Branch' },
    { id: 3003, team_id: 1, start_time: '09:00:00', end_time: '13:00:00', pause_time: '00:00:00', month, day: Math.min(21, daysInMonth), year, competence: 'member', working_site: 'Ghent Office', needed_work_site: 'Ghent Office' },
  ].filter((n) => n.day <= daysInMonth);

  return {
    success: true,
    userAvailabilities,
    userWorkInfo,
    userNeededWork,
  };
}

function mockGetUserInfoCalendar() {
  const now = new Date();
  return {
    success: true,
    userSites: [
      { id: 1, site_name: 'Brussels HQ' },
      { id: 2, site_name: 'Antwerp Branch' },
      { id: 3, site_name: 'Ghent Office' },
      { id: 4, site_name: 'Liège Depot' },
      { id: 5, site_name: 'Remote' },
      { id: 6, site_name: 'Client Site A' },
    ],
    user: demoUser,
    teams: [{ id: 1, name: 'Brussels Central', ...demoTeamInfo }],
    holidayRequests: [
      {
        id: 10,
        user_id: 1,
        start_date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-10`,
        end_date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-14`,
        status: 'Approved',
        request_date: new Date(now - 10 * 24 * 60 * 60 * 1000).toISOString().slice(0, 19).replace('T', ' '),
        approval_date: new Date(now - 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 19).replace('T', ' '),
        manager_id: 1,
        note: null,
        leave_type_id: 1,
      },
      {
        id: 11,
        user_id: 1,
        start_date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-20`,
        end_date: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-22`,
        status: 'Pending',
        request_date: now.toISOString().slice(0, 19).replace('T', ' '),
        approval_date: null,
        manager_id: null,
        note: 'Annual leave request',
        leave_type_id: null,
      },
    ],
  };
}

function mockGetUserInfoProfile() {
  return { success: true, user: demoUser };
}

function mockGetTeamData() {
  const teamMembers = [
    { user_id: 1, first_name: 'Christopher', last_name: 'Martin', email: 'christopher@example.com', phone_number: '+32123456789', creation_date: '2023-01-10T09:00:00', profile_picture_path: null, iban: 'BE68 5390 0754 7034', address: 'Rue de la Loi 42, 1000 Brussels', registration_id: 'REG-001', business_name: 'Martin & Co SPRL', bce: '0123456789', role: { team_id: 1, competences: getDemoCompetences() } },
    { user_id: 2, first_name: 'Alex', last_name: 'Dupont', email: 'alex@example.com', phone_number: '+32456789123', creation_date: '2023-02-15T10:30:00', profile_picture_path: null, iban: 'BE71 0961 2345 6769', address: 'Rue du Marché 12, 2000 Antwerp', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
    { user_id: 3, first_name: 'Sophie', last_name: 'Laurent', email: 'sophie@example.com', phone_number: '+32789123456', creation_date: '2023-03-20T08:00:00', profile_picture_path: null, iban: null, address: 'Avenue de la Gare 12, 4000 Liège', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
    { user_id: 4, first_name: 'Lucas', last_name: 'Peeters', email: 'lucas@example.com', phone_number: '+32471234567', creation_date: '2023-04-05T09:00:00', profile_picture_path: null, iban: 'BE39 0011 7890 1234', address: 'Koningin Astridlaan 8, 9000 Ghent', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
    { user_id: 5, first_name: 'Emma', last_name: 'Janssen', email: 'emma@example.com', phone_number: '+32495678901', creation_date: '2023-05-12T10:00:00', profile_picture_path: null, iban: 'BE42 3100 5678 9012', address: 'Leopoldstraat 22, 2000 Antwerp', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
    { user_id: 6, first_name: 'Thomas', last_name: 'Maes', email: 'thomas@example.com', phone_number: '+32468901234', creation_date: '2023-06-18T08:30:00', profile_picture_path: null, iban: null, address: 'Place Saint-Lambert 3, 4000 Liège', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
    { user_id: 7, first_name: 'Julie', last_name: 'Claes', email: 'julie@example.com', phone_number: '+32487654321', creation_date: '2023-08-01T09:00:00', profile_picture_path: null, iban: 'BE56 7890 1234 5678', address: 'Veldstraat 45, 9000 Ghent', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
    { user_id: 8, first_name: 'Nathan', last_name: 'Wouters', email: 'nathan@example.com', phone_number: '+32476543210', creation_date: '2024-01-15T10:00:00', profile_picture_path: null, iban: null, address: 'Bondgenotenlaan 10, 3000 Leuven', registration_id: null, business_name: null, bce: null, role: { team_id: 1, competences: { role: 'member', salaried: 'salaried' } } },
  ];
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = now.getFullYear();
  const fmt = (d) => d.toISOString().slice(0, 19).replace('T', ' ');
  const requestHolidays = [
    { id: 1, user_id: 1, start_date: `${yy}-${mm}-10`, end_date: `${yy}-${mm}-14`, status: 'Approved', request_date: fmt(new Date(now - 10 * 864e5)), approval_date: fmt(new Date(now - 5 * 864e5)), manager_id: 1, note: null, leave_type_id: 1 },
    { id: 2, user_id: 5, start_date: `${yy}-${mm}-20`, end_date: `${yy}-${mm}-22`, status: 'Pending', request_date: fmt(now), approval_date: null, manager_id: null, note: 'Family event', leave_type_id: 1 },
    { id: 3, user_id: 2, start_date: `${yy}-${mm}-08`, end_date: `${yy}-${mm}-09`, status: 'Approved', request_date: fmt(new Date(now - 12 * 864e5)), approval_date: fmt(new Date(now - 8 * 864e5)), manager_id: 1, note: null, leave_type_id: 1 },
    { id: 4, user_id: 6, start_date: `${yy}-${mm}-15`, end_date: `${yy}-${mm}-16`, status: 'Pending', request_date: fmt(new Date(now - 2 * 864e5)), approval_date: null, manager_id: null, note: null, leave_type_id: 2 },
    { id: 5, user_id: 7, start_date: `${yy}-${mm}-24`, end_date: `${yy}-${mm}-28`, status: 'Approved', request_date: fmt(new Date(now - 15 * 864e5)), approval_date: fmt(new Date(now - 10 * 864e5)), manager_id: 1, note: 'Vacation', leave_type_id: 1 },
  ];
  const leave_types = [
    { id: 1, name: 'Paid leave',   name_fr: 'Congé payé',      name_nl: 'Betaald verlof',   color: '#22c55e', deducts_balance: 1, is_system: true, translation_key: 'leaveTypePaidLeave' },
    { id: 2, name: 'Sick leave',   name_fr: 'Congé maladie',    name_nl: 'Ziekteverlof',     color: '#f97316', deducts_balance: 0, is_system: true, translation_key: 'leaveTypeSickLeave' },
    { id: 3, name: 'Unpaid leave', name_fr: 'Congé sans solde', name_nl: 'Onbetaald verlof', color: '#6b7280', deducts_balance: 0, is_system: true, translation_key: 'leaveTypeUnpaidLeave' },
    { id: 4, name: 'Comp time',    name_fr: 'Récupération',     name_nl: 'Compensatie',      color: '#8b5cf6', deducts_balance: 1, is_system: false, translation_key: null },
    { id: 5, name: 'Training',     name_fr: 'Formation',        name_nl: 'Opleiding',        color: '#0ea5e9', deducts_balance: 1, is_system: false, translation_key: null },
  ];
  return {
    competences: [
      { competence_id: 1, competence_name: 'member' },
      { competence_id: 2, competence_name: 'salaried' },
      { competence_id: 3, competence_name: 'manager' },
    ],
    sites: [
      { site_id: 1, site_name: 'Brussels HQ' },
      { site_id: 2, site_name: 'Antwerp Branch' },
      { site_id: 3, site_name: 'Ghent Office' },
      { site_id: 4, site_name: 'Liège Depot' },
      { site_id: 5, site_name: 'Remote' },
      { site_id: 6, site_name: 'Client Site A' },
    ],
    shifts: [
      { shift_id: 1, shift_start: '09:00:00', shift_end: '17:00:00', pause_time: '01:00:00' },
      { shift_id: 2, shift_start: '14:00:00', shift_end: '22:00:00', pause_time: '00:30:00' },
    ],
    teamMembers,
    teamInfo: {
      ...demoTeamInfo,
      team_id: 1,
      team_code: 'DEMO-ABC-123',
      ConfirmationDate: '15',
      time_zone: 'Europe/Brussels',
      ConfirmationHours: '12:00',
      ShowNeedsForMembers: true,
      number_of_members: 8,
    },
    requestHolidays,
    leave_types,
  };
}

/**
 * Team schedule data for a given month (Monthly / Weekly).
 * Generates realistic schedules: each person has a home site, consistent shift,
 * works Mon-Fri, with occasional WFH Fridays and a few unconfirmed/late arrivals.
 * body: { month, year, team_id }
 */
function mockTeamInfoMonths(body) {
  const month = typeof body.month === 'number' ? body.month : new Date().getMonth();
  const year = typeof body.year === 'number' ? body.year : new Date().getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Each member's typical schedule pattern
  // dayOff: 0=Sun,6=Sat — extra day off for part-timers (e.g. Wed off)
  const memberPatterns = [
    { user_id: 1, competence: getDemoTier() === 'worker' ? 'member' : 'manager', site: 'Brussels HQ', start: '09:00:00', end: '17:00:00', pause: '01:00:00', wfhDay: 5 }, // Christopher: Mon-Fri at HQ, WFH Fridays
    { user_id: 2, competence: 'member',  site: 'Antwerp Branch', start: '08:00:00', end: '16:00:00', pause: '01:00:00', wfhDay: null },    // Alex: early shift, Antwerp
    { user_id: 3, competence: 'member',  site: 'Brussels HQ',    start: '09:00:00', end: '17:00:00', pause: '01:00:00', wfhDay: 5 },      // Sophie: standard, HQ, WFH Fri
    { user_id: 4, competence: 'member',  site: 'Ghent Office',   start: '07:00:00', end: '15:00:00', pause: '00:30:00', wfhDay: null },    // Lucas: early shift, Ghent
    { user_id: 5, competence: 'member',  site: 'Brussels HQ',    start: '10:00:00', end: '18:00:00', pause: '01:00:00', wfhDay: null },    // Emma: late morning, HQ
    { user_id: 6, competence: 'member',  site: 'Liège Depot',    start: '06:00:00', end: '14:00:00', pause: '00:30:00', wfhDay: null },    // Thomas: early depot
    { user_id: 7, competence: 'member',  site: 'Ghent Office',   start: '09:00:00', end: '17:00:00', pause: '01:00:00', dayOff: 3 },       // Julie: Mon-Fri except Wed (4/5)
    { user_id: 8, competence: 'member',  site: 'Antwerp Branch', start: '14:00:00', end: '22:00:00', pause: '00:30:00', wfhDay: null },    // Nathan: afternoon/evening shift
  ];

  // Deterministic "random" based on day + user_id (no Math.random so data is stable)
  const seed = (day, uid) => ((day * 7 + uid * 13 + month * 3) % 17);

  const teamWorkInfo = [];
  const userAvailabilities = [];
  let workId = 4000;
  let availId = 5000;

  for (let day = 1; day <= daysInMonth; day++) {
    const dow = new Date(year, month, day).getDay(); // 0=Sun, 6=Sat
    if (dow === 0 || dow === 6) continue; // Skip weekends

    for (const p of memberPatterns) {
      // Skip if it's their extra day off
      if (p.dayOff && dow === p.dayOff) continue;

      const s = seed(day, p.user_id);

      // ~10% chance of day off (sick/leave) — skip this person this day
      if (s === 0) continue;

      // Determine if WFH day
      const isWfh = p.wfhDay && dow === p.wfhDay;
      const site = isWfh ? 'Remote' : p.site;
      const note = isWfh ? 'WFH' : null;

      // Occasionally send to Client Site A (seed-based, ~1 in 17 days)
      const isClient = !isWfh && s === 3;
      const actualSite = isClient ? 'Client Site A' : site;
      const actualNote = isClient ? 'Client visit' : note;

      // Past days get confirmed=1, some recent ones are unconfirmed
      const isPast = new Date(year, month, day) < new Date();
      const confirmed = isPast ? (s === 1 ? 0 : 1) : (s <= 2 ? 0 : 1);

      // Past confirmed shifts: ~30% have arrival/departure times (slight variance)
      let arrival = null, departure = null, realPause = null;
      if (isPast && confirmed === 1 && s <= 5) {
        const arrMin = (s % 3 === 0) ? -2 : (s % 5) + 1; // -2 to +5 min
        const depMin = (s % 2 === 0) ? 2 : -(s % 3);
        const [sh, sm] = p.start.split(':').map(Number);
        const [eh, em] = p.end.split(':').map(Number);
        arrival = `${String(sh).padStart(2,'0')}:${String(Math.max(0, sm + arrMin)).padStart(2,'0')}:00`;
        departure = `${String(eh).padStart(2,'0')}:${String(Math.max(0, em + depMin)).padStart(2,'0')}:00`;
        realPause = p.pause;
      }

      teamWorkInfo.push({
        id: ++workId, user_id: p.user_id, year, month, day,
        start_time: p.start, end_time: p.end,
        arrival_time: arrival, departure_time: departure,
        working_site: actualSite, competence: p.competence,
        pause_time: p.pause, confirmed, team_id: 1,
        note: actualNote, real_pause_time: realPause,
      });
    }

    // Availabilities: members declare availability on days they're not working
    for (const p of memberPatterns) {
      const s = seed(day, p.user_id);
      const isWorking = !(p.dayOff && dow === p.dayOff) && s !== 0;

      if (!isWorking) {
        // Not working this day — high chance of declaring availability
        if (s % 4 !== 1) {
          userAvailabilities.push({
            id: ++availId, user_id: p.user_id, year, month, day,
            start_time: p.start, end_time: p.end,
            available_sites: `${p.site},Remote`, night_shift: false,
          });
        }
      } else if (seed(day, p.user_id + 20) % 5 === 0) {
        // Working but also available for extra hours at another site
        const otherSites = ['Brussels HQ', 'Antwerp Branch', 'Ghent Office', 'Liège Depot', 'Remote'];
        const otherSite = otherSites[(day + p.user_id) % otherSites.length];
        userAvailabilities.push({
          id: ++availId, user_id: p.user_id, year, month, day,
          start_time: '18:00:00', end_time: '22:00:00',
          available_sites: otherSite, night_shift: false,
        });
      }
    }
  }

  // Needed work: unfilled slots on some weekdays (realistic staffing gaps)
  const neededSites = ['Brussels HQ', 'Antwerp Branch', 'Ghent Office', 'Liège Depot', 'Client Site A'];
  const teamNeededWork = [];
  let needId = 6000;
  for (let day = 1; day <= daysInMonth; day++) {
    const dow = new Date(year, month, day).getDay();
    if (dow === 0 || dow === 6) continue;
    // ~2-3 needed work entries per week
    if (seed(day, 99) % 3 !== 0) continue;
    const siteIdx = seed(day, 50) % neededSites.length;
    teamNeededWork.push({
      id: ++needId, team_id: 1,
      start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00',
      month, day, year, competence: 'member',
      working_site: neededSites[siteIdx], needed_work_site: neededSites[siteIdx],
    });
  }

  return {
    teamWorkInfo,
    userAvailabilities,
    teamNeededWork,
    teamInfo: mockGetTeamData(),
  };
}

function mockGetHolidays() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const holidays = [
    { year, month, day: 1 },
    { year, month, day: Math.min(15, daysInMonth) },
    { year, month, day: Math.min(daysInMonth, 25) },
  ];
  return { holidays };
}

function mockGetConfirmationHours() {
  return { confirmation_hours: 48 };
}

function mockGetLogs() {
  const now = new Date();
  const formatTs = (d) => d.toISOString().slice(0, 19).replace('T', ' ');
  return {
    success: true,
    logs: [
      { action: 'update_arrival_departure', performed_by: 2, user_id: 2, timestamp: formatTs(new Date(now - 2 * 864e5)), details: '09:05:00/17:12:00/00:45:00' },
      { action: 'update_work', performed_by: 1, user_id: 2, timestamp: formatTs(new Date(now - 5 * 864e5)), details: '09:00:00/17:00:00/01:00:00/Brussels HQ/member' },
      { action: 'update_arrival_departure', performed_by: 2, user_id: 2, timestamp: formatTs(new Date(now - 9 * 864e5)), details: '08:58:00/17:00:00/01:00:00' },
      { action: 'manager_update_arrival_departure', performed_by: 1, user_id: 2, timestamp: formatTs(new Date(now - 12 * 864e5)), details: '09:00:00/17:05:00/01:00:00' },
    ],
  };
}

function mockGetLogsTeam() {
  const now = new Date();
  const formatTs = (d) => d.toISOString().slice(0, 19).replace('T', ' ');
  return {
    success: true,
    logs: [
      { action: 'update_work', performed_by: 1, user_id: 2, timestamp: formatTs(new Date(now - 1 * 864e5)), details: '09:00:00/17:00:00/01:00:00/Brussels HQ/member' },
      { action: 'manager_update_arrival_departure', performed_by: 1, user_id: 3, timestamp: formatTs(new Date(now - 3 * 864e5)), details: '09:02:00/17:08:00/01:00:00' },
      { action: 'update_work', performed_by: 1, user_id: 6, timestamp: formatTs(new Date(now - 5 * 864e5)), details: '14:00:00/22:00:00/00:30:00/Liège Depot/member' },
      { action: 'delete_work', performed_by: 1, user_id: 4, timestamp: formatTs(new Date(now - 7 * 864e5)), details: '07:00:00/15:00:00/00:30:00/Ghent Office/member' },
    ],
  };
}

function mockDashboardInit(_body) {
  const now = new Date();
  const curMonth = now.getMonth();
  const curYear = now.getFullYear();
  const nextMonth = curMonth === 11 ? 0 : curMonth + 1;
  const nextYear = curMonth === 11 ? curYear + 1 : curYear;

  const curSchedule = mockGetUserSchedule({ month: curMonth, year: curYear });
  const nxtSchedule = mockGetUserSchedule({ month: nextMonth, year: nextYear });

  const tier = getDemoTier();
  const swapNow = new Date();

  // Worker: personal swaps only. Manager: team approval swaps only. Full: both.
  let mySwaps = null;
  let pendingApproval = null;

  if (tier === 'worker') {
    mySwaps = [
      { id: 502, requester_user_id: 4, first_name: 'Lucas', last_name: 'Peeters', target_user_id: 1, requester_work_id: 4004, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 12, start_time: '07:00:00', end_time: '15:00:00', pause_time: '00:30:00', working_site: 'Ghent Office', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
    ];
  } else if (tier === 'manager') {
    pendingApproval = [
      { id: 601, requester_user_id: 3, requester_first_name: 'Sophie', requester_last_name: 'Laurent', target_user_id: 6, target_first_name: 'Thomas', target_last_name: 'Maes', requester_work_id: 4010, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 18, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member', team_id: 1, status: 'pending_approval', created_at: swapNow.toISOString() },
      { id: 602, requester_user_id: 8, requester_first_name: 'Nathan', requester_last_name: 'Wouters', target_user_id: 2, target_first_name: 'Alex', target_last_name: 'Dupont', requester_work_id: 4012, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 22, start_time: '14:00:00', end_time: '22:00:00', pause_time: '00:30:00', working_site: 'Antwerp Branch', competence: 'member', team_id: 1, status: 'pending_approval', created_at: swapNow.toISOString() },
    ];
  } else {
    // Full: both personal swaps and team approvals
    mySwaps = [
      { id: 502, requester_user_id: 4, first_name: 'Lucas', last_name: 'Peeters', target_user_id: 1, requester_work_id: 4004, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 12, start_time: '07:00:00', end_time: '15:00:00', pause_time: '00:30:00', working_site: 'Ghent Office', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
      { id: 503, requester_user_id: 1, first_name: 'Emma', last_name: 'Janssen', target_user_id: 5, requester_work_id: 4005, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 19, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
    ];
    pendingApproval = [
      { id: 601, requester_user_id: 3, requester_first_name: 'Sophie', requester_last_name: 'Laurent', target_user_id: 6, target_first_name: 'Thomas', target_last_name: 'Maes', requester_work_id: 4010, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 18, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member', team_id: 1, status: 'pending_approval', created_at: swapNow.toISOString() },
    ];
  }

  return {
    user: {
      userId: 1,
      firstName: 'Christopher',
      lastName: 'Martin',
      is_admin: tier === 'full',
      notification_token: 'demo-push-token',
      language: localStorage.getItem('selectedLanguage') || 'en',
    },
    teams: mockCheckToken().teams,
    features: mockGetFeatures().features,
    schedule: {
      current: {
        year: curYear,
        month: curMonth,
        userWorkInfo: curSchedule.userWorkInfo,
        userNeededWork: curSchedule.userNeededWork,
        userAvailabilities: curSchedule.userAvailabilities,
      },
      next: {
        year: nextYear,
        month: nextMonth,
        userWorkInfo: nxtSchedule.userWorkInfo,
        userNeededWork: nxtSchedule.userNeededWork,
        userAvailabilities: nxtSchedule.userAvailabilities,
      },
    },
    swaps: {
      my_swaps: mySwaps,
      pending_approval: pendingApproval,
    },
    setup_status: tier !== 'worker' ? {
      shifts_count: 2,
      sites_count: 6,
      members_count: 8,
    } : null,
  };
}

function mockNeedsResponses(body) {
  const reqMonth = typeof body.month === 'number' ? body.month : new Date().getMonth();
  const reqYear = typeof body.year === 'number' ? body.year : new Date().getFullYear();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sites = ['Brussels HQ', 'Antwerp Branch', 'Ghent Office', 'Liège Depot'];
  const availableUsers = [
    { user_id: 2, first_name: 'Alex', last_name: 'Dupont' },
    { user_id: 3, first_name: 'Sophie', last_name: 'Laurent' },
    { user_id: 5, first_name: 'Emma', last_name: 'Janssen' },
    { user_id: 7, first_name: 'Julie', last_name: 'Claes' },
  ];

  const responses = [];
  // Generate for requested month and also next month to ensure there are always future entries
  const months = [
    { month: reqMonth, year: reqYear },
    { month: reqMonth === 11 ? 0 : reqMonth + 1, year: reqMonth === 11 ? reqYear + 1 : reqYear },
  ];

  for (const { month, year } of months) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const dow = new Date(year, month, day).getDay();
      if (dow === 0 || dow === 6) continue;
      const d = new Date(year, month, day);
      if (d < today) continue;
      if (((day * 7 + 99 * 13 + month * 3) % 17) % 5 !== 0) continue;
      const siteIdx = ((day * 7 + 50 * 13 + month * 3) % 17) % sites.length;
      const usersForDay = availableUsers.slice(0, 1 + (day % availableUsers.length));
      responses.push({
        needed_work_id: 6000 + month * 100 + day,
        day, month, year,
        start_time: '09:00:00', end_time: '17:00:00',
        competence: 'member',
        needed_work_site: sites[siteIdx],
        available_users: usersForDay,
      });
    }
  }

  return { responses };
}

function mockBulkBalance(body) {
  const userIds = body.user_ids || [1];
  const leaveTypeId = body.leave_type_id || 1;
  const balances = {};
  for (const uid of userIds) {
    const seed = (uid * 7 + leaveTypeId * 3) % 10;
    balances[uid] = { annual_days: 20 + seed, used_days: 2 + seed, remaining_days: 18 };
  }
  return { success: true, balances };
}

function mockInvoiceRates() {
  return {
    rates: [
      { user_id: 1, day_salary: 200, night_salary: 225, saturday_salary: 240, saturday_night_salary: 260, sunday_salary: 280, sunday_night_salary: 300, holiday_salary: 290, holiday_night_salary: 310 },
      { user_id: 2, day_salary: 180, night_salary: 205, saturday_salary: 220, saturday_night_salary: 240, sunday_salary: 260, sunday_night_salary: 280, holiday_salary: 270, holiday_night_salary: 290 },
      { user_id: 3, day_salary: 175, night_salary: 200, saturday_salary: 215, saturday_night_salary: 235, sunday_salary: 255, sunday_night_salary: 275, holiday_salary: 265, holiday_night_salary: 285 },
      { user_id: 4, day_salary: 170, night_salary: 195, saturday_salary: 210, saturday_night_salary: 230, sunday_salary: 250, sunday_night_salary: 270, holiday_salary: 260, holiday_night_salary: 280 },
      { user_id: 5, day_salary: 180, night_salary: 205, saturday_salary: 220, saturday_night_salary: 240, sunday_salary: 260, sunday_night_salary: 280, holiday_salary: 270, holiday_night_salary: 290 },
      { user_id: 6, day_salary: 170, night_salary: 195, saturday_salary: 210, saturday_night_salary: 230, sunday_salary: 250, sunday_night_salary: 270, holiday_salary: 260, holiday_night_salary: 280 },
      { user_id: 7, day_salary: 175, night_salary: 200, saturday_salary: 215, saturday_night_salary: 235, sunday_salary: 255, sunday_night_salary: 275, holiday_salary: 265, holiday_night_salary: 285 },
    ],
  };
}

function mockInvoiceSiteSalaries() {
  return { success: true, siteSalaries: [
    { user_id: 1, site_id: 1, day_salary: 12, night_salary: 14, saturday_salary: 14, saturday_night_salary: 16, sunday_salary: 16, sunday_night_salary: 18, holiday_salary: 18, holiday_night_salary: 20, commute_rate: 15 },
    { user_id: 3, site_id: 1, day_salary: 11, night_salary: 13, saturday_salary: 13, saturday_night_salary: 15, sunday_salary: 15, sunday_night_salary: 17, holiday_salary: 17, holiday_night_salary: 19, commute_rate: 12.50 },
  ] };
}

function mockInvoiceSiteRates() {
  return { success: true, siteRates: [
    { user_id: 1, site_id: 1, day_salary: 220, night_salary: 245, saturday_salary: 260, saturday_night_salary: 280, sunday_salary: 300, sunday_night_salary: 320, holiday_salary: 310, holiday_night_salary: 330, commute_rate: 25 },
    { user_id: 3, site_id: 1, day_salary: 195, night_salary: 220, saturday_salary: 235, saturday_night_salary: 255, sunday_salary: 275, sunday_night_salary: 295, holiday_salary: 285, holiday_night_salary: 305, commute_rate: 20 },
  ] };
}

function mockInvoiceMonthly() {
  return {
    success: true,
    members: [
      { user_id: 1, name: 'Christopher Martin', total_hours: 160.0, supplement_hours: 0, total_pay: 3200.00, salary_missing: false },
      { user_id: 2, name: 'Alex Dupont', total_hours: 154.0, supplement_hours: 0, total_pay: 2772.00, salary_missing: false },
      { user_id: 3, name: 'Sophie Laurent', total_hours: 160.0, supplement_hours: 0, total_pay: 2800.00, salary_missing: false },
      { user_id: 4, name: 'Lucas Peeters', total_hours: 157.5, supplement_hours: 7.5, total_pay: 2677.50, salary_missing: false },
      { user_id: 5, name: 'Emma Janssen', total_hours: 160.0, supplement_hours: 0, total_pay: 2880.00, salary_missing: false },
      { user_id: 6, name: 'Thomas Maes', total_hours: 157.5, supplement_hours: 7.5, total_pay: 2677.50, salary_missing: false },
      { user_id: 7, name: 'Julie Claes', total_hours: 128.0, supplement_hours: 0, total_pay: 2240.00, salary_missing: false },
      { user_id: 8, name: 'Nathan Wouters', total_hours: 157.5, supplement_hours: 7.5, total_pay: 0, salary_missing: true },
    ],
  };
}

function mockInvoiceMonthlySummary(body) {
  const year = body.year || new Date().getFullYear();
  const month = body.month !== undefined ? body.month : new Date().getMonth();
  return {
    success: true,
    year, month,
    total_hours: 1234.5,
    total_invoice: 21247.00,
    member_count: 8,
    members: [
      { user_id: 1, name: 'Christopher Martin', total_hours: 160.0, total_invoice: 3200.00, shifts: 22 },
      { user_id: 2, name: 'Alex Dupont', total_hours: 154.0, total_invoice: 2772.00, shifts: 21 },
      { user_id: 3, name: 'Sophie Laurent', total_hours: 160.0, total_invoice: 2800.00, shifts: 22 },
      { user_id: 4, name: 'Lucas Peeters', total_hours: 157.5, total_invoice: 2677.50, shifts: 21 },
      { user_id: 5, name: 'Emma Janssen', total_hours: 160.0, total_invoice: 2880.00, shifts: 22 },
      { user_id: 6, name: 'Thomas Maes', total_hours: 157.5, total_invoice: 2677.50, shifts: 21 },
      { user_id: 7, name: 'Julie Claes', total_hours: 128.0, total_invoice: 2240.00, shifts: 17 },
      { user_id: 8, name: 'Nathan Wouters', total_hours: 157.5, total_invoice: 0, shifts: 21 },
    ],
  };
}

function mockGetSalaries() {
  const year = new Date().getFullYear();
  const month = new Date().getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const teamMembers = [
    { id: 1, first_name: 'Christopher', last_name: 'Martin', email: 'christopher@example.com' },
    { id: 2, first_name: 'Alex',    last_name: 'Dupont',  email: 'alex@example.com' },
    { id: 3, first_name: 'Sophie',  last_name: 'Laurent', email: 'sophie@example.com' },
    { id: 4, first_name: 'Lucas',   last_name: 'Peeters', email: 'lucas@example.com' },
    { id: 5, first_name: 'Emma',    last_name: 'Janssen', email: 'emma@example.com' },
    { id: 6, first_name: 'Thomas',  last_name: 'Maes',    email: 'thomas@example.com' },
    { id: 7, first_name: 'Julie',   last_name: 'Claes',   email: 'julie@example.com' },
    { id: 8, first_name: 'Nathan',  last_name: 'Wouters', email: 'nathan@example.com' },
  ];

  const salaries = [
    { user_id: 1, day_salary: 120, night_salary: 135, saturday_salary: 140, saturday_night_salary: 155, sunday_salary: 160, sunday_night_salary: 175, holiday_salary: 170, holiday_night_salary: 185 },
    { user_id: 2, day_salary: 110, night_salary: 125, saturday_salary: 130, saturday_night_salary: 145, sunday_salary: 150, sunday_night_salary: 165, holiday_salary: 160, holiday_night_salary: 175 },
    { user_id: 3, day_salary: 105, night_salary: 120, saturday_salary: 125, saturday_night_salary: 140, sunday_salary: 145, sunday_night_salary: 160, holiday_salary: 155, holiday_night_salary: 170 },
    { user_id: 4, day_salary: 100, night_salary: 115, saturday_salary: 120, saturday_night_salary: 135, sunday_salary: 140, sunday_night_salary: 155, holiday_salary: 150, holiday_night_salary: 165 },
    { user_id: 5, day_salary: 110, night_salary: 125, saturday_salary: 130, saturday_night_salary: 145, sunday_salary: 150, sunday_night_salary: 165, holiday_salary: 160, holiday_night_salary: 175 },
    { user_id: 6, day_salary: 100, night_salary: 115, saturday_salary: 120, saturday_night_salary: 135, sunday_salary: 140, sunday_night_salary: 155, holiday_salary: 150, holiday_night_salary: 165 },
    { user_id: 7, day_salary: 105, night_salary: 120, saturday_salary: 125, saturday_night_salary: 140, sunday_salary: 145, sunday_night_salary: 160, holiday_salary: 155, holiday_night_salary: 170 },
    // user_id: 8 (Nathan Wouters) intentionally has no salary entry — demos the missing-rate warning
  ];

  const holidays = [
    { id: 1, year, month, day: 1 },
    { id: 2, year, month, day: Math.min(15, daysInMonth) },
    { id: 3, year, month, day: Math.min(daysInMonth, 25) },
  ];

  return {
    salaries,
    teamMembers,
    holidays,
    nightTime: '22:00:00',
    holidayInfo: { holiday_days: 20, used_holiday_days: 0 },
  };
}

function mockManageHolidayRequest() {
  return { success: true };
}

function mockRequestHoliday() {
  // Return a unique-enough ID so the newly created request can be deleted in demo mode
  return { success: true, holidayId: Date.now() % 100000 };
}

function mockSignIn() {
  return {
    success: true,
    validToken: true,
    userId: 1,
    notification_token: 'demo-push-token',
    first_name: 'Christopher',
    last_name: 'Martin',
    teams: {
      1: {
        team_name: 'Brussels Central',
        competences: getDemoCompetences(),
        ConfirmationDate: '15',
      },
    },
  };
}

function mockScheduleAvailableUsers() {
  return { user_ids: [1, 2, 3, 4, 5, 6, 7, 8] };
}

function mockScheduleAvailableShifts() {
  return {
    shifts: [
      { shift_id: 1, shift_start: '09:00:00', shift_end: '17:00:00', pause_time: '01:00:00' },
      { shift_id: 2, shift_start: '14:00:00', shift_end: '22:00:00', pause_time: '00:30:00' },
    ],
  };
}

const DEFAULT_FEATURES = {
  needed_work: true,
  work_confirmation: true,
  time_tracking: true,
  leave: true,
  salary: true,
  swap_requests: true,
  schedule_templates: true,
  overtime_rules: true,
  swap_approval: true,
  user_invoice: true,
  team_invoice: true,
  multiple_sites: true,
  forced_competence: false,
  site_salary: false,
  show_needs_for_members: false,
};

function mockGetFeatures() {
  // Read from localStorage so toggled flags persist in demo mode
  const cached = localStorage.getItem('team_features_1');
  if (cached) {
    try {
      return { success: true, features: { ...DEFAULT_FEATURES, ...JSON.parse(cached) } };
    } catch { /* fall through */ }
  }
  return { success: true, features: { ...DEFAULT_FEATURES } };
}

// ─── Schedule Templates (demo) ──────────────────────────────────────
let _mockTemplates = [
  {
    id: 1,
    name: 'Standard Week',
    entry_count: 5,
    created_at: new Date().toISOString(),
    entries: [
      { day_of_week: 1, user_id: 1, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member' },
      { day_of_week: 2, user_id: 2, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Antwerp Branch', competence: 'member' },
      { day_of_week: 3, user_id: 3, start_time: '08:00:00', end_time: '16:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member' },
      { day_of_week: 4, user_id: 1, start_time: '14:00:00', end_time: '22:00:00', pause_time: '00:30:00', working_site: 'Liège Depot', competence: 'member' },
      { day_of_week: 5, user_id: 4, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member' },
    ],
  },
];
let _mockTemplateNextId = 2;

// ─── Overtime Rules (demo) ──────────────────────────────────────────
let _mockOvertimeRules = [
  { id: 1, name: 'Weekly 40h', threshold_type: 'weekly', threshold_hours: 40, multiplier: 1.5, enabled: true, site_id: null },
  { id: 2, name: 'Daily 8h', threshold_type: 'daily', threshold_hours: 8, multiplier: 1.5, enabled: false, site_id: null },
  { id: 3, name: 'Brussels OT', threshold_type: 'weekly', threshold_hours: 38, multiplier: 1.75, enabled: true, site_id: 1 },
];
let _mockOvertimeRuleNextId = 4;

function mockScheduleTemplates() {
  return { templates: _mockTemplates.map(({ entries: _entries, ...rest }) => rest) };
}

function mockSaveScheduleTemplate(body) {
  const id = _mockTemplateNextId++;
  const tpl = { id, name: body.name, entry_count: (body.entries || []).length, created_at: new Date().toISOString(), entries: body.entries || [] };
  _mockTemplates.push(tpl);
  return { id };
}

function mockGetScheduleTemplate(body) {
  const tpl = _mockTemplates.find(t => t.id === body.template_id);
  return { template: tpl || null };
}

function mockDeleteScheduleTemplate(body) {
  _mockTemplates = _mockTemplates.filter(t => t.id !== body.template_id);
  return { success: true };
}

function mockApplyScheduleTemplate(body) {
  const tpl = _mockTemplates.find(t => t.id === body.template_id);
  const count = tpl ? tpl.entries.length : 0;
  return { created: count, skipped: 0 };
}

// ─── Needed Work Templates (demo) ──────────────────────────────────
let _mockNWTemplates = [];
let _mockNWTemplateNextId = 1;

function mockNeededWorkTemplates() {
  return { templates: _mockNWTemplates.map(({ entries: _entries, ...rest }) => rest) };
}

function mockSaveNeededWorkTemplate(body) {
  const id = _mockNWTemplateNextId++;
  const tpl = { id, name: body.name, entry_count: (body.entries || []).length, created_at: new Date().toISOString(), entries: body.entries || [] };
  _mockNWTemplates.push(tpl);
  return { id };
}

function mockDeleteNeededWorkTemplate(body) {
  _mockNWTemplates = _mockNWTemplates.filter(t => t.id !== body.template_id);
  return { success: true };
}

function mockApplyNeededWorkTemplate(body) {
  const tpl = _mockNWTemplates.find(t => t.id === body.template_id);
  const count = tpl ? tpl.entries.length : 0;
  return { created: count, skipped: [] };
}

function mockGetWorkHistory(body) {
  const year = body.year || new Date().getFullYear();
  const month = body.month !== undefined ? body.month : new Date().getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return {
    workHistory: [
      { id: 1, user_id: body.user_id || 1, year, month, day: Math.min(5, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', confirmed: 1 },
      { id: 2, user_id: body.user_id || 1, year, month, day: Math.min(12, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Antwerp Branch', confirmed: 1 },
      { id: 3, user_id: body.user_id || 1, year, month, day: Math.min(19, daysInMonth), start_time: '14:00:00', end_time: '22:00:00', pause_time: '00:30:00', working_site: 'Liège Depot', confirmed: 1 },
      { id: 4, user_id: body.user_id || 1, year, month, day: Math.min(24, daysInMonth), start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Remote', confirmed: 1 },
    ].filter((w) => w.day <= daysInMonth),
  };
}

/**
 * Return a mock Response-like object for the given URL and optional parsed body.
 * Returns null if no mock is defined (caller will use real fetch).
 */
export function getMockResponse(url, body = {}) {
  if (!url) return null;

  let path;
  try {
    path = new URL(url).pathname.replace(/^\//, '');
  } catch {
    return null;
  }
  let data = null;

  switch (path) {
    case 'account/me':
      data = mockCheckToken();
      break;
    case 'dashboard/init':
      data = mockDashboardInit(body);
      break;
    case 'account/update-language':
      data = { success: true };
      break;
    case 'a/team-members':
      data = {
        success: true,
        members: [
          { id: 2, first_name: 'Alex', last_name: 'Dupont' },
          { id: 3, first_name: 'Sophie', last_name: 'Laurent' },
          { id: 4, first_name: 'Lucas', last_name: 'Peeters' },
          { id: 5, first_name: 'Emma', last_name: 'Janssen' },
          { id: 6, first_name: 'Thomas', last_name: 'Maes' },
          { id: 7, first_name: 'Julie', last_name: 'Claes' },
          { id: 8, first_name: 'Nathan', last_name: 'Wouters' },
        ],
      };
      break;
    case 'a/get-user-schedule':
      data = mockGetUserSchedule(body);
      break;
    case 'a/get-user-info':
      data = mockGetUserInfoCalendar();
      break;
    case 'account/get-user-info':
      data = mockGetUserInfoProfile();
      break;
    case 'team-settings/get-team-data':
      data = mockGetTeamData();
      break;
    case 'a/team-info-months':
      data = mockTeamInfoMonths(body);
      break;
    case 'leaves/get-holidays':
      data = mockGetHolidays();
      break;
    case 'team-settings/get-confirmation-hours':
      data = mockGetConfirmationHours();
      break;
    case 'a/get-logs':
      data = mockGetLogs();
      break;
    case 'a/get-logs-team':
      data = mockGetLogsTeam();
      break;
    case 'salaries/get-salaries':
      data = mockGetSalaries();
      break;
    case 'a/manage-holiday-request':
      data = mockManageHolidayRequest();
      break;
    case 'a/request-holiday':
      data = mockRequestHoliday();
      break;
    case 'account/signin':
      data = mockSignIn();
      break;
    case 'account/signout':
      data = { success: true };
      break;
    case 'account/all-teams':
      // Not a super admin in demo mode
      data = null;
      return {
        ok: false,
        status: 403,
        json: () => Promise.resolve({ error: 'Forbidden' }),
        text: () => Promise.resolve('{"error":"Forbidden"}'),
        blob: () => Promise.resolve(new Blob(['{"error":"Forbidden"}'])),
        clone: function clone() { return this; },
      };
    case 'account/update-notification':
      data = { success: true };
      break;
    case 'create-team':
    case 'account/join-team':
      data = {
        success: true,
        teamId: 1,
        userId: 1,
        notification_token: 'demo-push-token',
        teams: { 1: { team_name: 'Brussels Central', competences: getDemoCompetences(), ConfirmationDate: '15' } },
      };
      break;
    case 'account/join-team-by-code':
      data = { success: true, team_name: 'Brussels Central' };
      break;
    case 'account/reset-password':
    case 'account/reset-password-confirm':
      data = { success: true };
      break;
    case 'schedule/available-users':
      data = mockScheduleAvailableUsers();
      break;
    case 'schedule/available-shifts':
      data = mockScheduleAvailableShifts();
      break;
    case 'team-settings/get-features':
      data = mockGetFeatures();
      break;
    case 'team-settings/update-features': {
      // Merge toggled flags into localStorage so they persist in demo mode
      const current = mockGetFeatures().features;
      const updated = { ...current, ...(body.features || {}) };
      localStorage.setItem('team_features_1', JSON.stringify(updated));
      data = { success: true };
      break;
    }
    case 'schedule/templates':
      data = mockScheduleTemplates();
      break;
    case 'schedule/save-template':
      data = mockSaveScheduleTemplate(body);
      break;
    case 'schedule/get-template':
      data = mockGetScheduleTemplate(body);
      break;
    case 'schedule/delete-template':
      data = mockDeleteScheduleTemplate(body);
      break;
    case 'schedule/apply-template':
      data = mockApplyScheduleTemplate(body);
      break;
    case 'schedule/needed-work-templates':
      data = mockNeededWorkTemplates();
      break;
    case 'schedule/save-needed-work-template':
      data = mockSaveNeededWorkTemplate(body);
      break;
    case 'schedule/delete-needed-work-template':
      data = mockDeleteNeededWorkTemplate(body);
      break;
    case 'schedule/apply-needed-work-template':
      data = mockApplyNeededWorkTemplate(body);
      break;
    case 'schedule/get-work-history':
      data = mockGetWorkHistory(body);
      break;
    case 'a/manager-update-work-arrival-departure':
      data = { success: true };
      break;
    case 'account/update-date_of_birth':
    case 'account/update-nationality':
    case 'account/update-gender':
    case 'account/update-tax_id':
    case 'account/update-emergency_contact':
      data = { success: true };
      break;
    case 'team-settings/update-member-contract':
      data = { success: true };
      break;
    case 'team-settings/add-placeholder-members': {
      const newMembers = (body.members || []).map((m, i) => ({
        id: 100 + i,
        first_name: m.first_name,
        last_name: m.last_name,
        is_placeholder: true,
      }));
      data = { members: newMembers };
      break;
    }
    case 'team-settings/link-member-email':
      data = { success: true, invite_sent: true };
      break;
    case 'team-settings/unlink-member-email':
      data = { success: true };
      break;
    case 'account/delete-account':
      data = { success: true };
      break;
    case 'account/transfer-ownership':
      data = { success: true };
      break;
    case 'team-settings/get-email-preferences':
      data = { preferences: { signup_member: true, shift_change: true, holiday_update: false, swap_update: true } };
      break;
    case 'team-settings/update-email-preference':
      data = { success: true };
      break;
    case 'salaries/shift-pay':
      data = { pay: 120, hours: 7.5, detail: { day_hours: 6.5, night_hours: 1, day_rate: 15, night_rate: 18, day_pay: 97.5, night_pay: 18 } };
      break;
    case 'invoices/shift-pay':
      data = { pay: 150, hours: 7.5, detail: { day_hours: 6.5, night_hours: 1, day_rate: 20, night_rate: 24, day_pay: 130, night_pay: 24 } };
      break;
    case 'schedule/assignable-competences':
      data = { competences: [{ competence_id: 1, competence_name: 'member' }, { competence_id: 2, competence_name: 'salaried' }] };
      break;
    case 'schedule/conflict-check':
      data = { conflict: false };
      break;
    case 'salaries/overtime-rules':
      data = { rules: _mockOvertimeRules };
      break;
    case 'salaries/add-overtime-rule': {
      const ruleId = _mockOvertimeRuleNextId++;
      const newRule = { id: ruleId, name: body.name, threshold_type: body.threshold_type, threshold_hours: body.threshold_hours, multiplier: body.multiplier, enabled: body.enabled ?? true, site_id: body.site_id ?? null };
      _mockOvertimeRules.push(newRule);
      data = { id: ruleId };
      break;
    }
    case 'salaries/update-overtime-rule':
      _mockOvertimeRules = _mockOvertimeRules.map(r => r.id === body.id ? { ...r, ...body } : r);
      data = { success: true };
      break;
    case 'salaries/delete-overtime-rule':
      _mockOvertimeRules = _mockOvertimeRules.filter(r => r.id !== body.id);
      data = { success: true };
      break;
    case 'schedule/needs-responses':
      data = mockNeedsResponses(body);
      break;
    case 'leaves/bulk-balance':
      data = mockBulkBalance(body);
      break;
    case 'salaries/get-site-salaries':
      data = mockInvoiceSiteSalaries();
      break;
    case 'invoices/team-monthly':
      data = mockInvoiceMonthly();
      break;
    case 'invoices/get-rates':
      data = mockInvoiceRates();
      break;
    case 'invoices/get-site-rates':
      data = mockInvoiceSiteRates();
      break;
    case 'salaries/monthly-summary':
      data = mockInvoiceMonthlySummary(body);
      break;
    case 'invoices/monthly-summary':
      data = mockInvoiceMonthlySummary(body);
      break;
    case 'a/pending-approval-swaps': {
      const apprNow = new Date();
      data = {
        success: true,
        swaps: [
          { id: 601, requester_user_id: 3, requester_first_name: 'Sophie', requester_last_name: 'Laurent', target_user_id: 6, target_first_name: 'Thomas', target_last_name: 'Maes', requester_work_id: 4010, year: apprNow.getFullYear(), month: apprNow.getMonth(), day: 18, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member', team_id: 1, status: 'pending_approval', created_at: apprNow.toISOString() },
        ],
      };
      break;
    }
    default:
      // Generic success for write endpoints (update-*, delete-*, etc.) so UI doesn't break
      if (
        path.startsWith('a/update-') ||
        path.startsWith('a/delete-') ||
        path.startsWith('account/update-') ||
        path.startsWith('account/delete-') ||
        path.startsWith('team-settings/update-') ||
        path.startsWith('team-settings/delete-') ||
        path.startsWith('team-settings/add-') ||
        path.startsWith('team-settings/reset-') ||
        path.startsWith('salaries/lock-') ||
        path.startsWith('salaries/update-') ||
        path.startsWith('salaries/delete-') ||
        path.startsWith('a/confirm-') ||
        path === 'a/request-swap' ||
        path === 'a/respond-swap' ||
        path === 'a/cancel-swap' ||
        path === 'a/approve-swap' ||
        path.startsWith('leaves/set-leave-type-balance') ||
        path.startsWith('leaves/add-') ||
        path.startsWith('leaves/delete-') ||
        path.startsWith('leaves/update-') ||
        path.startsWith('invoices/update-') ||
        path.startsWith('invoices/delete-') ||
        path.startsWith('account/store-token') ||
        path.startsWith('account/signup')
      ) {
        data = { success: true };
      } else if (path === 'a/pending-swaps') {
        // Manager view: all pending swaps for the team
        const swapNow = new Date();
        data = {
          success: true,
          swaps: [
            { id: 501, requester_user_id: 2, first_name: 'Alex', last_name: 'Dupont', target_user_id: 5, requester_work_id: 4002, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 5, start_time: '08:00:00', end_time: '16:00:00', pause_time: '01:00:00', working_site: 'Antwerp Branch', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
            { id: 502, requester_user_id: 4, first_name: 'Lucas', last_name: 'Peeters', target_user_id: 1, requester_work_id: 4004, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: 12, start_time: '07:00:00', end_time: '15:00:00', pause_time: '00:30:00', working_site: 'Ghent Office', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
          ],
        };
      } else if (path === 'a/my-swaps') {
        const swapNow = new Date();
        const futureDay1 = Math.min(swapNow.getDate() + 5, 28);
        const futureDay2 = Math.min(swapNow.getDate() + 12, 28);
        const pastDay1 = Math.max(swapNow.getDate() - 10, 1);
        const pastDay2 = Math.max(swapNow.getDate() - 20, 1);
        data = {
          success: true,
          swaps: [
            // Received swap — shows Approve / Reject buttons
            { id: 502, requester_user_id: 4, first_name: 'Lucas', last_name: 'Peeters', target_user_id: 1, requester_work_id: 4004, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: futureDay1, start_time: '07:00:00', end_time: '15:00:00', pause_time: '00:30:00', working_site: 'Ghent Office', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
            // Sent swap — shows "Awaiting response" badge
            { id: 503, requester_user_id: 1, first_name: 'Emma', last_name: 'Janssen', target_user_id: 5, requester_work_id: 4005, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: futureDay2, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member', team_id: 1, status: 'pending', created_at: swapNow.toISOString() },
            // Past swaps — hidden by default, shown via history toggle
            { id: 504, requester_user_id: 3, first_name: 'Sophie', last_name: 'Laurent', target_user_id: 1, requester_work_id: 4010, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: pastDay1, start_time: '08:00:00', end_time: '16:00:00', pause_time: '01:00:00', working_site: 'Antwerp Branch', competence: 'member', team_id: 1, status: 'accepted', created_at: swapNow.toISOString() },
            { id: 505, requester_user_id: 1, first_name: 'Alex', last_name: 'Dupont', target_user_id: 2, requester_work_id: 4011, year: swapNow.getFullYear(), month: swapNow.getMonth(), day: pastDay2, start_time: '09:00:00', end_time: '17:00:00', pause_time: '01:00:00', working_site: 'Brussels HQ', competence: 'member', team_id: 1, status: 'rejected', created_at: swapNow.toISOString() },
          ],
        };
      } else if (path === 'a/holiday-balance') {
        data = { success: true, annual_days: 20, used_days: 3, remaining_days: 17 };
      } else if (path === 'leaves/leave-type-balance') {
        // deducts_balance types return remaining; non-deducting return used only
        const ltId = (body || {}).leave_type_id;
        const userId = (body || {}).user_id || 1;
        const seed = (userId * 7 + ltId * 3) % 10;
        if (ltId === 1) {
          data = { success: true, annual_days: 20 + seed, used_days: 2 + seed, remaining_days: 18 };
        } else if (ltId === 4) {
          data = { success: true, annual_days: 5 + (seed % 3), used_days: seed % 4, remaining_days: 5 + (seed % 3) - (seed % 4) };
        } else if (ltId === 5) {
          data = { success: true, annual_days: 3, used_days: seed % 2, remaining_days: 3 - (seed % 2) };
        } else {
          data = { success: true, annual_days: null, used_days: 3, remaining_days: null };
        }
      } else if (path === 'salaries/export') {
        data = 'user_id,name,total_hours,total_pay,commute_total,grand_total\n1,Christopher Martin,160,1920,330,2250\n2,Alex Dupont,154,1694,0,1694\n3,Sophie Laurent,160,1680,275,1955\n4,Lucas Peeters,157.5,1575,0,1575\n5,Emma Janssen,160,1760,0,1760\n6,Thomas Maes,157.5,1575,0,1575\n7,Julie Claes,128,1344,0,1344\n8,Nathan Wouters,157.5,0,0,0\n';
      } else if (path === 'salaries/team-monthly') {
        data = {
          success: true,
          members: [
            { user_id: 1, name: 'Christopher Martin', total_hours: 160.0, supplement_hours: 0,   total_pay: 1920.00, commute_total: 330.00, grand_total: 2250.00, overtime_hours: 8, overtime_pay: 180,   total_with_overtime: 2430.00, salary_missing: false },
            { user_id: 2, name: 'Alex Dupont',        total_hours: 154.0, supplement_hours: 0,   total_pay: 1694.00, commute_total: 0,      grand_total: 1694.00, overtime_hours: 0, overtime_pay: 0,     total_with_overtime: 1694.00, salary_missing: false },
            { user_id: 3, name: 'Sophie Laurent',     total_hours: 160.0, supplement_hours: 0,   total_pay: 1680.00, commute_total: 275.00, grand_total: 1955.00, overtime_hours: 4, overtime_pay: 72,    total_with_overtime: 2027.00, salary_missing: false },
            { user_id: 4, name: 'Lucas Peeters',      total_hours: 157.5, supplement_hours: 7.5, total_pay: 1575.00, commute_total: 0,      grand_total: 1575.00, overtime_hours: 0, overtime_pay: 0,     total_with_overtime: 1575.00, salary_missing: false },
            { user_id: 5, name: 'Emma Janssen',       total_hours: 160.0, supplement_hours: 0,   total_pay: 1760.00, commute_total: 0,      grand_total: 1760.00, overtime_hours: 0, overtime_pay: 0,     total_with_overtime: 1760.00, salary_missing: false },
            { user_id: 6, name: 'Thomas Maes',        total_hours: 157.5, supplement_hours: 7.5, total_pay: 1575.00, commute_total: 0,      grand_total: 1575.00, overtime_hours: 0, overtime_pay: 0,     total_with_overtime: 1575.00, salary_missing: false },
            { user_id: 7, name: 'Julie Claes',        total_hours: 128.0, supplement_hours: 0,   total_pay: 1344.00, commute_total: 0,      grand_total: 1344.00, overtime_hours: 0, overtime_pay: 0,     total_with_overtime: 1344.00, salary_missing: false },
            { user_id: 8, name: 'Nathan Wouters',     total_hours: 157.5, supplement_hours: 7.5, total_pay: 0,       commute_total: 0,      grand_total: 0,       overtime_hours: 0, overtime_pay: 0,     total_with_overtime: 0,       salary_missing: true  },
          ],
        };
      }
      break;
  }

  if (data === null) {
    // Catch-all: never leak to real API in demo mode — return generic success
    // so unmocked endpoints don't cause network errors or CORS failures.
    console.debug('[demo] unmocked endpoint:', path);
    data = { success: true };
  }

  const textContent = typeof data === 'string' ? data : JSON.stringify(data);
  const isJson = typeof data !== 'string';
  return {
    ok: true,
    status: 200,
    headers: new Headers({ 'content-type': isJson ? 'application/json' : 'text/plain' }),
    json: () => Promise.resolve(data),
    text: () => Promise.resolve(textContent),
    blob: () => Promise.resolve(new Blob([textContent], { type: 'application/octet-stream' })),
    clone: function clone() {
      return getMockResponse(url, body);
    },
  };
}
